import { Box, Grid, Text } from '@chakra-ui/layout';
import { Link, SkeletonText } from '@chakra-ui/react';
import classNames from 'classnames';
import { startCase } from 'lodash';
import React, { useMemo } from 'react';

import { Loading } from '@revelio/core';
import { FormatType, getFormatter, useChartTheme } from '@revelio/replots';
import { Tag } from '@revelio/ui';

import { useTrialUserBlurLogic } from '../../../hooks/useTrialUserBlurLogic';
import { normalizeProfileUrl } from '../../../utils/normalizeProfileUrl';
import BlurredCell from '../../BlurredCell';
import {
  MarkdownDescription,
  shouldRenderMarkdown,
} from '../MarkdownDescription/MarkdownDescription';
import styles from './FullProfileInfo.module.css';
import themed from './FullProfileInfo.themed.module.css';
import { ProfileBadge } from './ProfileBadge';
import type { FullProfileInfoData, SalaryRange } from './types';
import { useFormattedWorkExperience } from './useFormattedWorkExperience';

const GRID_COLUMN_GAP = 25;
const RIGHT_COLUMN_MIN_WIDTH = 180;
const GRID_TEMPLATE_COLUMNS = `minmax(0, calc(100% - ${RIGHT_COLUMN_MIN_WIDTH}px)) minmax(${RIGHT_COLUMN_MIN_WIDTH}px, 1fr)`;
const salaryFormatter = getFormatter(FormatType.CURRENCY);

const getPillClassName = (value: string): string => {
  const normalized = value.toLowerCase();
  if (normalized.includes('high') || normalized.includes('very high')) {
    return `${styles.pill} ${styles.pillHigh}`;
  }
  if (normalized.includes('medium') || normalized.includes('moderate')) {
    return `${styles.pill} ${styles.pillMedium}`;
  }
  if (normalized.includes('low') || normalized.includes('very low')) {
    return `${styles.pill} ${styles.pillLow}`;
  }
  return '';
};

const renderValue = (value: string): React.ReactNode => {
  const pillClassName = getPillClassName(value);
  const displayValue = startCase(value);

  if (pillClassName) {
    return <Text className={pillClassName}>{displayValue}</Text>;
  }

  return <Text className={styles.enrichedDataValue}>{displayValue}</Text>;
};

type NameParts = {
  firstName: string;
  lastName: string;
  fullName: string;
};

const getHeaderNameParts = (
  header: FullProfileInfoData['header']
): NameParts => {
  const providedFirstName = header.firstName?.trim() ?? '';
  const providedLastName = header.lastName?.trim() ?? '';

  if (providedFirstName || providedLastName) {
    return {
      firstName: providedFirstName,
      lastName: providedLastName,
      fullName: [providedFirstName, providedLastName].filter(Boolean).join(' '),
    };
  }

  const trimmedName = header.name.trim();
  if (!trimmedName) {
    return { firstName: '', lastName: '', fullName: '' };
  }

  const nameParts = trimmedName.split(/\s+/);
  if (nameParts.length === 1) {
    return {
      firstName: nameParts[0] ?? '',
      lastName: '',
      fullName: trimmedName,
    };
  }

  return {
    firstName: nameParts.slice(0, -1).join(' '),
    lastName: nameParts[nameParts.length - 1] ?? '',
    fullName: trimmedName,
  };
};

/** Trial users see masked placeholders over the raw contact fields. Shared by
 *  both layouts so the masking rules can't drift between them. */
const getMaskedHeader = (
  header: FullProfileInfoData['header'],
  isTrialUser: boolean
): FullProfileInfoData['header'] => {
  if (!isTrialUser) {
    return header;
  }

  const maskedValues = header.maskedValues ?? {};
  return {
    ...header,
    lastName: maskedValues.lastName ?? header.lastName ?? '',
    workEmail: maskedValues.workEmail ?? header.workEmail,
    personalEmail: maskedValues.personalEmail ?? header.personalEmail,
    phone: maskedValues.phone ?? header.phone,
    profileUrl: maskedValues.profileUrl ?? header.profileUrl,
  };
};

type EmailRenderProps = {
  email: string;
  isRestricted: boolean;
  encryptedValue?: string;
  onMouseEnter: () => void;
  userKey?: string;
  fieldKey: string;
};

const renderEmailLink = ({
  email,
  isRestricted,
  encryptedValue,
  onMouseEnter,
  userKey,
  fieldKey,
}: EmailRenderProps): React.ReactNode => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    return <Text className={styles.sectionDetails}>{email}</Text>;
  }

  if (isRestricted) {
    return (
      <Box className={styles.sectionDetails}>
        <BlurredCell
          value={trimmedEmail}
          encryptedValue={encryptedValue}
          onMouseEnter={onMouseEnter}
          allowUnblur={false}
          isCompact={false}
          userKey={userKey}
          fieldKey={fieldKey}
        />
      </Box>
    );
  }

  return (
    <Link
      as="a"
      href={`mailto:${trimmedEmail}`}
      color="lightBlue.600"
      fontSize="12px"
      _hover={{ textDecoration: 'underline' }}
      display="block"
    >
      {trimmedEmail}
    </Link>
  );
};

type PhoneRenderProps = {
  phone: string;
  isRestricted: boolean;
  encryptedValue?: string;
  onMouseEnter: () => void;
  userKey?: string;
  fieldKey: string;
};

const renderPhoneValue = ({
  phone,
  isRestricted,
  encryptedValue,
  onMouseEnter,
  userKey,
  fieldKey,
}: PhoneRenderProps): React.ReactNode => {
  if (!phone.trim()) {
    return <Text className={styles.sectionDetails}>{phone}</Text>;
  }

  if (isRestricted) {
    return (
      <Box className={styles.sectionDetails}>
        <BlurredCell
          value={phone}
          encryptedValue={encryptedValue}
          onMouseEnter={onMouseEnter}
          allowUnblur={false}
          isCompact={false}
          userKey={userKey}
          fieldKey={fieldKey}
        />
      </Box>
    );
  }

  return <Text className={styles.sectionDetails}>{phone}</Text>;
};

type ProfileUrlRenderProps = {
  profileUrl: string;
  isRestricted: boolean;
  encryptedValue?: string;
  onMouseEnter: () => void;
  userKey?: string;
  fieldKey: string;
};

const renderProfileUrl = ({
  profileUrl,
  isRestricted,
  encryptedValue,
  onMouseEnter,
  userKey,
  fieldKey,
}: ProfileUrlRenderProps): React.ReactNode => {
  const normalized = normalizeProfileUrl(profileUrl);
  if (!normalized) {
    return null;
  }

  if (isRestricted) {
    return (
      <Box className={styles.sectionDetails}>
        <BlurredCell
          value={normalized.displayValue}
          encryptedValue={encryptedValue}
          onMouseEnter={onMouseEnter}
          allowUnblur={false}
          isCompact={false}
          userKey={userKey}
          fieldKey={fieldKey}
        />
      </Box>
    );
  }

  return (
    <Link
      as="a"
      href={normalized.href}
      color="lightBlue.600"
      fontSize="12px"
      _hover={{ textDecoration: 'underline' }}
      display="block"
      isExternal
      rel="noopener noreferrer"
    >
      {normalized.displayValue}
    </Link>
  );
};

const formatSalaryRange = ({ min, max }: SalaryRange): string => {
  if (min === max) {
    return salaryFormatter(min);
  }

  return `${salaryFormatter(min)} - ${salaryFormatter(max)}`;
};

interface FullProfileInfoProps {
  data: FullProfileInfoData;
}

export const FullProfileInfoLoading = ({
  minHeight = '400px',
}: {
  minHeight?: number | string;
}) => {
  return (
    <Box
      className={styles.loadingContainer}
      minHeight={minHeight}
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      data-testid="full-profile-info-loading"
    >
      <Loading size="lg" />
    </Box>
  );
};

const FullProfileInfoLegacy = ({ data }: FullProfileInfoProps) => {
  const { workExperience: formattedWorkExperience, formattingIndices } =
    useFormattedWorkExperience(data.workExperience);
  const { isTrialUser, showBlurToast } = useTrialUserBlurLogic();
  const userKey = data.originalUserId ?? data.userId;
  const maskedHeader = useMemo(
    () => getMaskedHeader(data.header, isTrialUser),
    [data.header, isTrialUser]
  );

  const dataWithFormattedDescriptions = {
    ...data,
    workExperience: formattedWorkExperience,
  };
  const nameParts = getHeaderNameParts(maskedHeader);
  const shouldBlurLastName = isTrialUser && nameParts.lastName.length > 0;

  return (
    <Box className={styles.container}>
      {/* Top Section - Two Column Grid */}
      <Grid
        gridColumnGap={`${GRID_COLUMN_GAP}px`}
        gridRowGap={0}
        templateColumns={GRID_TEMPLATE_COLUMNS}
        width="100%"
        alignItems="start"
        marginTop={0}
        paddingTop={0}
        margin={0}
        padding={0}
      >
        {/* Header Info */}
        <Box className={styles.contentBox}>
          {shouldBlurLastName ? (
            <Box
              className={styles.name}
              display="flex"
              alignItems="baseline"
              columnGap="6px"
            >
              {nameParts.firstName && <span>{nameParts.firstName}</span>}
              <Box display="inline-block">
                <BlurredCell
                  value={nameParts.lastName}
                  encryptedValue={maskedHeader.originalValues?.lastName}
                  onMouseEnter={showBlurToast}
                  allowUnblur={false}
                  isCompact={false}
                  userKey={userKey}
                  fieldKey="last_name"
                />
              </Box>
            </Box>
          ) : (
            <Text className={styles.name}>{nameParts.fullName}</Text>
          )}
          <Text className={styles.role}>{maskedHeader.role}</Text>
          {renderPhoneValue({
            phone: maskedHeader.phone,
            isRestricted: isTrialUser,
            encryptedValue: maskedHeader.originalValues?.phone,
            onMouseEnter: showBlurToast,
            userKey,
            fieldKey: 'phone_number',
          })}
          {renderEmailLink({
            email: maskedHeader.workEmail,
            isRestricted: isTrialUser,
            encryptedValue: maskedHeader.originalValues?.workEmail,
            onMouseEnter: showBlurToast,
            userKey,
            fieldKey: 'email',
          })}
          <Text className={styles.sectionDetails}>{maskedHeader.location}</Text>
          {renderEmailLink({
            email: maskedHeader.personalEmail,
            isRestricted: isTrialUser,
            encryptedValue: maskedHeader.originalValues?.personalEmail,
            onMouseEnter: showBlurToast,
            userKey,
            fieldKey: 'personal_email',
          })}
          {maskedHeader.profileUrl.trim().length > 0 &&
            renderProfileUrl({
              profileUrl: maskedHeader.profileUrl,
              isRestricted: isTrialUser,
              encryptedValue: maskedHeader.originalValues?.profileUrl,
              onMouseEnter: showBlurToast,
              userKey,
              fieldKey: 'linkedin_url',
            })}
        </Box>

        {/* Metadata (Gray Card) */}
        <Box className={styles.grayCard}>
          {(data.metadata.salary.min > 0 || data.metadata.salary.max > 0) && (
            <Box className={styles.enrichedData}>
              <Text className={styles.enrichedDataLabel}>Salary</Text>
              <Text
                className={styles.enrichedDataValuePlain}
                color="text.primary"
              >
                {formatSalaryRange(data.metadata.salary)}
              </Text>
            </Box>
          )}
          {data.metadata.prestige && (
            <Box className={styles.enrichedData}>
              <Text className={styles.enrichedDataLabel}>Prestige</Text>
              {renderValue(data.metadata.prestige)}
            </Box>
          )}
          {data.metadata.flightRisk && (
            <Box className={styles.enrichedData}>
              <Text className={styles.enrichedDataLabel}>Flight Risk</Text>
              {renderValue(data.metadata.flightRisk)}
            </Box>
          )}
        </Box>
      </Grid>

      {/* Work Experience Section */}
      {dataWithFormattedDescriptions.workExperience.length > 0 && (
        <>
          {/* Work Experience Header - Full Width */}
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Work Experience</Text>
          </Box>

          {/* Work Experience Entries */}
          {dataWithFormattedDescriptions.workExperience.map((entry, index) => (
            <React.Fragment key={`work-experience-${index}`}>
              {/* Horizontal divider before work experience (except first) */}
              {index > 0 && (
                <Box className={styles.dividerBox}>
                  <Box className={styles.divider} />
                </Box>
              )}

              {/* Two Column Grid for Work Experience Entry */}
              <Grid
                gridColumnGap={`${GRID_COLUMN_GAP}px`}
                templateColumns={GRID_TEMPLATE_COLUMNS}
                width="100%"
                alignItems="start"
              >
                {/* Left side - Work experience details */}
                <Box className={styles.contentBox}>
                  <Text className={styles.sectionDate}>
                    {entry.startDate} - {entry.endDate}
                  </Text>
                  <Text className={styles.sectionTitle}>{entry.role}</Text>
                  <Text className={styles.sectionDetails}>
                    {entry.positionLocation}
                  </Text>
                  <Box
                    className={`${styles.sectionDetails} ${styles.description}`}
                  >
                    {(() => {
                      if (entry.isFormatted) {
                        return (
                          <MarkdownDescription
                            description={entry.description}
                          />
                        );
                      }

                      if (formattingIndices.has(index)) {
                        return <SkeletonText noOfLines={3} spacing="3" />;
                      }

                      if (shouldRenderMarkdown(entry.description, false)) {
                        return (
                          <MarkdownDescription
                            description={entry.description}
                          />
                        );
                      }

                      return (
                        <Text className={styles.descriptionText}>
                          {entry.description}
                        </Text>
                      );
                    })()}
                  </Box>
                </Box>

                {/* Right side - Enriched data (Gray Card) */}
                <Box className={styles.grayCard}>
                  {entry.jobFamily && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Job Family
                      </Text>
                      <Text className={styles.enrichedDataValue}>
                        {startCase(entry.jobFamily)}
                      </Text>
                    </Box>
                  )}
                  {entry.seniority && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Seniority
                      </Text>
                      {renderValue(entry.seniority)}
                    </Box>
                  )}
                  {entry.companyPrestige && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Company Prestige
                      </Text>
                      {renderValue(entry.companyPrestige)}
                    </Box>
                  )}
                  {entry.tenure && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>Tenure</Text>
                      {renderValue(entry.tenure)}
                    </Box>
                  )}
                  {(entry.salary.min > 0 || entry.salary.max > 0) && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>Salary</Text>
                      <Text className={styles.enrichedDataValue}>
                        {formatSalaryRange(entry.salary)}
                      </Text>
                    </Box>
                  )}
                  {entry.employmentRelationship && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Employment Type
                      </Text>
                      {renderValue(entry.employmentRelationship)}
                    </Box>
                  )}
                  {entry.workSchedule && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>
                        Work Schedule
                      </Text>
                      {renderValue(entry.workSchedule)}
                    </Box>
                  )}
                  {/* Position-level skills hidden for now. */}
                  {/* {entry.skills.length > 0 && (
                    <Box className={styles.enrichedData}>
                      <Text className={styles.enrichedDataLabel}>Skills</Text>
                      <Text className={styles.enrichedDataValue}>
                        {entry.skills
                          .map((skill) => startCase(skill))
                          .join(', ')}
                      </Text>
                    </Box>
                  )} */}
                </Box>
              </Grid>
            </React.Fragment>
          ))}
        </>
      )}

      {/* Education Section */}
      {data.education.length > 0 && (
        <>
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Education</Text>
          </Box>

          {data.education.map((entry, index) => (
            <React.Fragment key={`education-${index}`}>
              {index > 0 && (
                <Box className={styles.dividerBox}>
                  <Box className={styles.divider} />
                </Box>
              )}

              <Box className={styles.contentBox}>
                <Text className={styles.sectionDate}>
                  {entry.startDate} - {entry.endDate || 'Present'}
                </Text>
                <Text className={styles.sectionTitle}>{entry.institution}</Text>
                <Text className={styles.sectionDetails}>{entry.degree}</Text>
                {entry.fieldOfStudy && (
                  <Text className={styles.sectionDetails}>
                    {entry.fieldOfStudy}
                  </Text>
                )}
                {entry.description && (
                  <Box
                    className={`${styles.sectionDetails} ${styles.description}`}
                  >
                    <Text className={styles.descriptionText}>
                      {entry.description}
                    </Text>
                  </Box>
                )}
              </Box>
            </React.Fragment>
          ))}
        </>
      )}

      {/* Skills Section */}
      {(() => {
        const filteredSkills = (data.skills || []).filter(
          (skill) =>
            skill && typeof skill === 'string' && skill.trim().length > 0
        );
        return filteredSkills.length > 0 ? (
          <>
            <Box className={styles.dividerBoxHeader}>
              <Text className={styles.categoryHeader}>Skills</Text>
            </Box>

            <Box className={styles.contentBox}>
              <Box display="flex" flexWrap="wrap" gap="4px">
                {filteredSkills.map((skill, index) => (
                  <Text
                    key={`skill-${index}`}
                    display="inline-block"
                    padding="0px 5px"
                    borderRadius="md"
                    backgroundColor="white"
                    border="1px solid"
                    borderColor="gray.300"
                    color="#2d426a"
                    fontSize="xs"
                  >
                    {startCase(String(skill))}
                  </Text>
                ))}
              </Box>
            </Box>
          </>
        ) : null;
      })()}

      {/* Publications Section */}
      {data.publications.length > 0 && (
        <>
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Publications</Text>
          </Box>

          {data.publications.map((entry, index) => (
            <React.Fragment key={`publication-${index}`}>
              {index > 0 && (
                <Box className={styles.dividerBox}>
                  <Box className={styles.divider} />
                </Box>
              )}

              <Box className={styles.contentBox}>
                <Text className={styles.sectionDate}>{entry.date}</Text>
                <Text className={styles.sectionTitle}>{entry.title}</Text>
                <Text className={styles.sectionDetails}>{entry.source}</Text>
                {entry.description && (
                  <Box
                    className={`${styles.sectionDetails} ${styles.description}`}
                  >
                    <Text className={styles.descriptionText}>
                      {entry.description}
                    </Text>
                  </Box>
                )}
              </Box>
            </React.Fragment>
          ))}
        </>
      )}

      {/* Languages Section */}
      {data.languages.length > 0 && (
        <>
          <Box className={styles.dividerBoxHeader}>
            <Text className={styles.categoryHeader}>Languages</Text>
          </Box>

          <Box className={styles.contentBox}>
            {data.languages.map((entry, index) => (
              <Text key={`language-${index}`} className={styles.sectionDetails}>
                {entry.language} - {entry.proficiency}
              </Text>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

const hasSalary = (salary: SalaryRange): boolean =>
  salary.min > 0 || salary.max > 0;

/**
 * Dashboard 2026 redesign of the profile popout, rendered when a themed
 * appearance is active. Reuses the same data shape and helpers as the legacy
 * layout; only the presentation differs. Native elements + CSS-module classes
 * keep colors token-driven so they flip with body[data-theme].
 */
const FullProfileInfoThemed = ({ data }: FullProfileInfoProps) => {
  const { isTrialUser, showBlurToast } = useTrialUserBlurLogic();
  const userKey = data.originalUserId ?? data.userId;
  const maskedHeader = useMemo(
    () => getMaskedHeader(data.header, isTrialUser),
    [data.header, isTrialUser]
  );

  const nameParts = getHeaderNameParts(maskedHeader);
  const shouldBlurLastName = isTrialUser && nameParts.lastName.length > 0;

  const renderContact = (
    value: string,
    fieldKey: string,
    encryptedValue: string | undefined,
    href?: string
  ): React.ReactNode => {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (isTrialUser) {
      return (
        <span className={themed.link}>
          <BlurredCell
            value={trimmed}
            encryptedValue={encryptedValue}
            onMouseEnter={showBlurToast}
            allowUnblur={false}
            isCompact={false}
            userKey={userKey}
            fieldKey={fieldKey}
          />
        </span>
      );
    }
    if (href) {
      return (
        <a className={themed.link} href={href}>
          {trimmed}
        </a>
      );
    }
    return <span className={themed.link}>{trimmed}</span>;
  };

  const renderProfileUrl = (): React.ReactNode => {
    const trimmed = maskedHeader.profileUrl.trim();
    if (!trimmed) {
      return null;
    }
    const normalized = normalizeProfileUrl(trimmed);
    if (!normalized) {
      return null;
    }
    if (isTrialUser) {
      return (
        <span className={themed.link}>
          <BlurredCell
            value={normalized.displayValue}
            encryptedValue={maskedHeader.originalValues?.profileUrl}
            onMouseEnter={showBlurToast}
            allowUnblur={false}
            isCompact={false}
            userKey={userKey}
            fieldKey="linkedin_url"
          />
        </span>
      );
    }
    return (
      <a
        className={themed.link}
        href={normalized.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {normalized.displayValue}
      </a>
    );
  };

  const skills = (data.skills || []).filter(
    (skill) => skill && typeof skill === 'string' && skill.trim().length > 0
  );

  return (
    <div className={themed.container}>
      {/* Header */}
      <div className={themed.header}>
        <div className={themed.headerContent}>
          <div className={themed.info}>
            {shouldBlurLastName ? (
              <div className={classNames(themed.name, themed.nameRow)}>
                {nameParts.firstName && <span>{nameParts.firstName}</span>}
                <span>
                  <BlurredCell
                    value={nameParts.lastName}
                    encryptedValue={maskedHeader.originalValues?.lastName}
                    onMouseEnter={showBlurToast}
                    allowUnblur={false}
                    isCompact={false}
                    userKey={userKey}
                    fieldKey="last_name"
                  />
                </span>
              </div>
            ) : (
              <p className={themed.name}>{nameParts.fullName}</p>
            )}
            {maskedHeader.role && (
              <p className={themed.subtle}>{maskedHeader.role}</p>
            )}
            {maskedHeader.location && (
              <p className={themed.subtle}>{maskedHeader.location}</p>
            )}
          </div>
          <div className={themed.contact}>
            {renderContact(
              maskedHeader.workEmail,
              'email',
              maskedHeader.originalValues?.workEmail,
              `mailto:${maskedHeader.workEmail.trim()}`
            )}
            {renderContact(
              maskedHeader.personalEmail,
              'personal_email',
              maskedHeader.originalValues?.personalEmail,
              `mailto:${maskedHeader.personalEmail.trim()}`
            )}
            {renderContact(
              maskedHeader.phone,
              'phone_number',
              maskedHeader.originalValues?.phone
            )}
            {renderProfileUrl()}
          </div>
        </div>

        <div className={themed.metaCard}>
          {hasSalary(data.metadata.salary) && (
            <div className={themed.metaRow}>
              <span className={themed.metaLabel}>Salary:</span>
              <span className={themed.salary}>
                {formatSalaryRange(data.metadata.salary)}
              </span>
            </div>
          )}
          {data.metadata.prestige && (
            <div className={themed.metaRow}>
              <span className={themed.metaLabel}>Prestige:</span>
              <ProfileBadge value={data.metadata.prestige} />
            </div>
          )}
          {data.metadata.flightRisk && (
            <div className={themed.metaRow}>
              <span className={themed.metaLabel}>Flight Risk:</span>
              <ProfileBadge value={data.metadata.flightRisk} />
            </div>
          )}
        </div>
      </div>

      {/* Work Experience */}
      {data.workExperience.length > 0 && (
        <div className={themed.section}>
          <p className={themed.sectionTitle}>Work Experience</p>
          <div className={themed.entries}>
            {data.workExperience.map((entry, index) => (
              <div className={themed.entry} key={`work-experience-${index}`}>
                <div className={themed.entryInfo}>
                  <span className={themed.subtle}>
                    {entry.startDate} - {entry.endDate}
                  </span>
                  <span className={themed.entryRole}>{entry.role}</span>
                  {entry.positionLocation && (
                    <span className={themed.subtle}>
                      {entry.positionLocation}
                    </span>
                  )}
                </div>
                {(entry.companyPrestige || hasSalary(entry.salary)) && (
                  <div className={themed.metaCard}>
                    {entry.companyPrestige && (
                      <div className={themed.metaRow}>
                        <span className={themed.metaLabel}>
                          Company Prestige:
                        </span>
                        <ProfileBadge value={entry.companyPrestige} />
                      </div>
                    )}
                    {hasSalary(entry.salary) && (
                      <div className={themed.metaRow}>
                        <span className={themed.metaLabel}>Salary:</span>
                        <span className={themed.salary}>
                          {formatSalaryRange(entry.salary)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education */}
      {data.education.length > 0 && (
        <div className={themed.section}>
          <p className={themed.sectionTitle}>Education</p>
          <div className={themed.entries}>
            {data.education.map((entry, index) => (
              <div className={themed.entry} key={`education-${index}`}>
                <div className={themed.entryInfo}>
                  <span className={themed.subtle}>
                    {entry.startDate} - {entry.endDate || 'Present'}
                  </span>
                  <span className={themed.entryRole}>{entry.institution}</span>
                  {entry.degree && (
                    <span className={themed.subtle}>{entry.degree}</span>
                  )}
                  {entry.fieldOfStudy && (
                    <span className={themed.subtle}>{entry.fieldOfStudy}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <div className={themed.section}>
          <p className={themed.sectionTitle}>Skills</p>
          <div className={themed.skills}>
            {skills.map((skill, index) => (
              <Tag
                key={`skill-${index}`}
                size="sm"
                colorScheme="gray"
                variant="subtle"
              >
                {startCase(String(skill))}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Publications */}
      {data.publications.length > 0 && (
        <div className={themed.section}>
          <p className={themed.sectionTitle}>Publications</p>
          <div className={themed.entries}>
            {data.publications.map((entry, index) => (
              <div className={themed.entryInfo} key={`publication-${index}`}>
                <span className={themed.subtle}>{entry.date}</span>
                <span className={themed.entryRole}>{entry.title}</span>
                {entry.source && (
                  <span className={themed.subtle}>{entry.source}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Languages */}
      {data.languages.length > 0 && (
        <div className={themed.section}>
          <p className={themed.sectionTitle}>Languages</p>
          <div className={themed.entryInfo}>
            {data.languages.map((entry, index) => (
              <span className={themed.subtle} key={`language-${index}`}>
                {entry.language} - {entry.proficiency}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const FullProfileInfo = ({ data }: FullProfileInfoProps) => {
  const { isNewTheme } = useChartTheme();
  return isNewTheme ? (
    <FullProfileInfoThemed data={data} />
  ) : (
    <FullProfileInfoLegacy data={data} />
  );
};
